import { create } from "zustand";
import { persist } from "zustand/middleware";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Theme = "light" | "dark" | "system";
type SidebarState = "expanded" | "collapsed" | "hidden";

const SIDEBAR_CYCLE: SidebarState[] = ["expanded", "collapsed", "hidden"];

interface UiState {
  theme: Theme;
  sidebarState: SidebarState;
  sidebarCollapsed: boolean;
  activeTab: number;
  language: string;
  topBarPinned: boolean;

  setTheme: (theme: Theme) => void;
  setSidebarState: (state: SidebarState) => void;
  cycleSidebarState: () => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setActiveTab: (tab: number) => void;
  setLanguage: (lang: string) => void;
  toggleTopBarPin: () => void;
}

// ---------------------------------------------------------------------------
// Store (persisted)
// ---------------------------------------------------------------------------

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      theme: "system",
      sidebarState: "expanded",
      sidebarCollapsed: false,
      activeTab: 0,
      language: "it",
      topBarPinned: false,

      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
      },

      setSidebarState: (sidebarState) => {
        set({ sidebarState, sidebarCollapsed: sidebarState !== "expanded" });
      },

      cycleSidebarState: () => {
        set((s) => {
          const idx = SIDEBAR_CYCLE.indexOf(s.sidebarState);
          const next = SIDEBAR_CYCLE[(idx + 1) % SIDEBAR_CYCLE.length];
          return { sidebarState: next, sidebarCollapsed: next !== "expanded" };
        });
      },

      toggleSidebar: () => {
        set((s) => {
          const idx = SIDEBAR_CYCLE.indexOf(s.sidebarState);
          const next = SIDEBAR_CYCLE[(idx + 1) % SIDEBAR_CYCLE.length];
          return { sidebarState: next, sidebarCollapsed: next !== "expanded" };
        });
      },

      setSidebarCollapsed: (collapsed) => {
        const sidebarState = collapsed ? "collapsed" : "expanded";
        set({ sidebarState, sidebarCollapsed: collapsed });
      },

      setActiveTab: (tab) => {
        set({ activeTab: Math.max(0, Math.min(3, tab)) });
      },

      setLanguage: (language) => {
        set({ language });
      },

      toggleTopBarPin: () => {
        set((s) => ({ topBarPinned: !s.topBarPinned }));
      },
    }),
    { name: "lexe-admin-ui" },
  ),
);

// ---------------------------------------------------------------------------
// Theme side-effect
// ---------------------------------------------------------------------------

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.classList.remove("light", "dark");

  if (theme === "system") {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    root.classList.add(prefersDark ? "dark" : "light");
  } else {
    root.classList.add(theme);
  }
}

// Apply theme on load
const savedTheme = useUiStore.getState().theme;
applyTheme(savedTheme);
